import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { ChevronRight, ChevronDown, BookOpen, Search, X } from 'lucide-react';
import toast from 'react-hot-toast';

// Компонент для отображения текста с изображениями и видео (аналогично LiveEditManual)
const SimpleContentRenderer = ({ content }) => {
  if (!content) {
    return null;
  }
  
  // Обрабатываем видео ссылки (точно как в LiveEditManual)
  let processedContent = content
    .replace(/\[([^\]]*)\]\((https?:\/\/[^)]+)\)/g, (match, text, url) => {
      // Проверяем, является ли это ссылкой на видео
      if (url.includes('youtube.com') || url.includes('youtu.be') || 
          url.includes('vk.com/video') || url.includes('vkvideo.ru') || 
          url.includes('rutube.ru') || url.includes('tiktok.com') || 
          url.includes('dailymotion.com')) {
        return `[VIDEO:${url}:${text}]`;
      }
      return match; // Обычная ссылка
    });
  
  // Обрабатываем изображения
  processedContent = processedContent
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (match, alt, url) => {
      // Если это локальный путь, добавляем префикс API
      const imageUrl = url.startsWith('/uploads/') ? `/api/upload/images/${url.split('/').pop()}` : url;
      
      const imageHtml = `<div class="my-4 flex justify-center">
        <img src="${imageUrl}" alt="${alt}" class="max-w-full h-auto rounded-lg shadow-sm border border-gray-200 dark:border-gray-600" style="max-height: 400px;" onerror="this.style.display='none'; this.nextElementSibling.style.display='block';" />
        <div style="display:none;" class="text-center text-gray-500 dark:text-gray-400 p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
          <div class="text-sm">Не удалось загрузить изображение</div>
          <div class="text-xs mt-1 text-gray-400">${imageUrl}</div>
        </div>
      </div>`;
      
      return imageHtml;
    });
  
  // Обрабатываем видео (точно как в LiveEditManual)
  processedContent = processedContent
    .replace(/\[VIDEO:(https?:\/\/[^:]+):([^\]]+)\]/g, (match, url, title) => {
      if (url.includes('youtube.com') || url.includes('youtu.be')) {
        const videoId = url.includes('youtu.be/') 
          ? url.split('youtu.be/')[1]?.split('?')[0]
          : url.split('v=')[1]?.split('&')[0];
        
        if (videoId) {
          const iframeHtml = `<div class="my-4 flex justify-center">
            <iframe 
              width="560" 
              height="315" 
              src="https://www.youtube.com/embed/${videoId}" 
              title="${title}"
              frameborder="0" 
              allowfullscreen
              class="w-full max-w-2xl mx-auto rounded-lg shadow-lg"
              style="aspect-ratio: 16/9; max-width: 100%; height: auto;"
            ></iframe>
          </div>`;
          return iframeHtml;
        }
      }
      
      if (url.includes('vk.com/video') || url.includes('vkvideo.ru')) {
        let vkUrl = url;
        if (url.includes('vk.com/video')) {
          vkUrl = url.replace('vk.com/video', 'vk.com/video_ext.php');
        } else if (url.includes('vkvideo.ru')) {
          vkUrl = url.replace('vkvideo.ru', 'vk.com/video_ext.php');
        }
        
        return `<div class="my-4 flex justify-center">
          <iframe 
            src="${vkUrl}" 
            width="640" 
            height="360" 
            frameborder="0" 
            allowfullscreen
            class="w-full max-w-2xl mx-auto rounded-lg shadow-lg"
            style="aspect-ratio: 16/9; max-width: 100%; height: auto;"
          ></iframe>
        </div>`;
      }
      
      if (url.includes('rutube.ru/video/')) {
        const videoId = url.split('rutube.ru/video/')[1]?.split('/')[0];
        if (videoId) {
          return `<div class="my-4 flex justify-center">
            <iframe 
              width="720" 
              height="405" 
              src="https://rutube.ru/play/embed/${videoId}" 
              frameborder="0" 
              webkitAllowFullScreen 
              mozallowfullscreen 
              allowfullscreen
              loading="lazy"
              referrerpolicy="no-referrer-when-downgrade"
              class="w-full max-w-2xl mx-auto rounded-lg shadow-lg"
              style="aspect-ratio: 16/9; max-width: 100%; height: auto;"
            ></iframe>
          </div>`;
        }
      }
      
      return match;
    });
  
  // Заменяем переносы строк на <br>, но только в тексте (не внутри HTML тегов)
  processedContent = processedContent.replace(/\n(?![^<]*>)/g, '<br>');
  
  return (
    <div 
      className="prose max-w-none text-gray-700 dark:text-gray-300 leading-relaxed"
      dangerouslySetInnerHTML={{ __html: processedContent }}
    />
  );
};

const PublicManual = () => {
  const { slug } = useParams();
  const [manual, setManual] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);

  useEffect(() => {
    fetchManual();
  }, [slug]);

  useEffect(() => {
    if (manual && manual.items) {
      performSearch(searchQuery);
    }
  }, [searchQuery, manual]);


  const fetchManual = async () => {
    try {
      console.log('🔍 Загружаем публичный мануал с slug:', slug);
      console.log('🔍 URL запроса:', `/api/manual/public/${slug}`);
      
      const response = await axios.get(`/api/manual/public/${slug}`);
      console.log('✅ Мануал загружен:', response.data);
      
      setManual(response.data);
      setFilteredItems(response.data.items || []);
      // Автоматически выбираем первый элемент, если есть
      if (response.data.items && response.data.items.length > 0) {
        setSelectedItem(response.data.items[0]);
      }
    } catch (error) {
      console.error('Ошибка загрузки мануала:', error);
      console.error('Детали ошибки:', error.response?.data);
      toast.error('Мануал не найден');
    } finally {
      setLoading(false);
    }
  };

  const performSearch = (query) => {
    if (!query.trim()) {
      setFilteredItems(manual.items || []);
      return;
    }

    const searchLower = query.toLowerCase();
    
    const searchInItem = (item) => {
      const titleMatch = item.title.toLowerCase().includes(searchLower);
      const contentMatch = item.content.toLowerCase().includes(searchLower);
      
      // Проверяем детей
      const matchingChildren = item.children ? 
        item.children.map(child => searchInItem(child)).filter(child => child) : [];
      
      if (titleMatch || contentMatch || matchingChildren.length > 0) {
        return {
          ...item,
          children: matchingChildren
        };
      }
      return null;
    };

    const filtered = (manual.items || []).map(item => searchInItem(item)).filter(item => item);
    setFilteredItems(filtered);
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  const scrollToElement = (itemId) => {
    setTimeout(() => {
      const element = document.getElementById(`item-${itemId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const toggleExpanded = (itemId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const renderMenuItem = (item, level = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(item._id);
    const isSelected = selectedItem && selectedItem._id === item._id;

    return (
      <div key={item._id}>
        <div
          className={`py-2 px-3 cursor-pointer hover:bg-gray-100 rounded-lg mb-1 flex items-center justify-between ${
            isSelected ? 'bg-primary-100 text-primary-700' : 'text-gray-700'
          } ${level > 0 ? 'ml-4' : ''}`}
          onClick={() => {
            // Для всех элементов - прокручиваем к ним в контенте
            scrollToElement(item._id);
            
            // Устанавливаем элемент как выбранный
            setSelectedItem(item);
            
            // Если есть подразделы - разворачиваем их
            if (hasChildren) {
              toggleExpanded(item._id);
            }
          }}
        >
          <div className="flex items-center space-x-2">
            {hasChildren && (
              isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
            )}
            <span className="font-medium" dangerouslySetInnerHTML={{ __html: item.title }} />
          </div>
        </div>
        
        {isExpanded && hasChildren && (
          <div className="ml-4">
            {item.children.map(child => renderMenuItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderMarkdownContent = (content) => {
    // Проверяем, что content существует
    if (!content) {
      return '';
    }
    
    // Простой парсер markdown для изображений
    let html = content.toString();
    
    // Обрабатываем изображения в формате ![alt](url)
    const imageRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;
    
    html = html.replace(imageRegex, (match, alt, url) => {
      // Экранируем URL для безопасности
      const safeUrl = url.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      const safeAlt = alt.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      
      return `<div class="my-4 flex justify-center">
        <div class="relative">
          <img src="${safeUrl}" alt="${safeAlt}" class="max-w-full h-auto rounded-lg shadow-sm border border-gray-200 dark:border-gray-600" style="max-height: 400px;" onerror="this.style.display='none'; this.nextElementSibling.style.display='block';" />
          <div style="display:none;" class="text-center text-gray-500 dark:text-gray-400 p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
            <div class="text-sm">Не удалось загрузить изображение</div>
            <div class="text-xs mt-1 text-gray-400">${safeUrl}</div>
          </div>
        </div>
      </div>`;
    });
    
    // Заменяем переносы строк на <br>
    html = html.replace(/\n/g, '<br>');
    
    return html;
  };

  const renderItemContent = (item, level = 0) => {
    return (
      <div 
        key={item._id}
        id={`item-${item._id}`}
        className={`${level > 0 ? 'ml-6' : ''}`}
      >
        {level > 0 && <hr className="my-4 border-gray-200 dark:border-gray-600" />}
        
        <div className="py-4">
          <h2 className={`font-bold text-gray-900 dark:text-gray-100 mb-4 ${level === 0 ? 'text-2xl' : 'text-xl'}`}>
            <span dangerouslySetInnerHTML={{ __html: item.title }} />
          </h2>
          
          <SimpleContentRenderer content={item.content} />
        </div>

        {/* Рендерим подразделы */}
        {item.children && item.children.length > 0 && (
          <div className="space-y-4">
            {item.children.map(child => renderItemContent(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!manual) {
    return (
      <div className="text-center py-12">
        <BookOpen className="h-16 w-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Мануал не найден</h2>
        <p className="text-gray-600 dark:text-gray-400">Возможно, мануал был удален или не является публичным.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
        <h1 
          className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2"
          dangerouslySetInnerHTML={{ __html: manual.title }}
        />
        {manual.description && (
          <p className="text-gray-600 dark:text-gray-400">{manual.description}</p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Боковое меню */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sticky top-4">
            {/* Поиск */}
            <div className="relative mb-4">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                placeholder="Поиск по разделам..."
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>
            
            <div className="space-y-1">
              {filteredItems && filteredItems.map(item => renderMenuItem(item))}
              {searchQuery && filteredItems.length === 0 && (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
                  Ничего не найдено по запросу "{searchQuery}"
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Основной контент */}
        <div className="lg:col-span-3">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            {selectedItem ? (
              <div>
                {renderItemContent(selectedItem)}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <BookOpen className="h-12 w-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
                <p>Выберите раздел из меню слева для просмотра содержимого.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicManual;
